import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Fuse from "fuse.js";
import portalApi, { fmtINR, fmtQty } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { ChevronRight, ChevronLeft, ShoppingCart, Star, Sparkles, Plus, Minus,
         AlertTriangle, ShieldCheck, Loader2, CalendarDays, ClipboardList, X, Search, Grid3x3, Mic } from "lucide-react";
import PortalVoiceCart from "./PortalVoiceCart";

// Steps in the guided flow
const STEP_INTRO = -1;
const STEP_REVIEW = -2;

export default function PortalOrder() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { client } = usePortalAuth();

    const [catalog, setCatalog] = useState({ invoice_type: "B2B", categories: [] });
    const [lastOrder, setLastOrder] = useState({ line_items: [], invoice_number: "" });
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(STEP_INTRO);
    // cart: { [itemId]: { name, category, unit, rate, tax_percent, hsn, qty } }
    const [cart, setCart] = useState({});
    const [reorderOf, setReorderOf] = useState(null);   // {id, number} when reordering a past invoice
    const [deliveryDate, setDeliveryDate] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotItems, setForgotItems] = useState([]);
    // Custom items — free-typed items the client wants that aren't in the catalog.
    // Sent alongside the order; admin later fills in HSN/GST/price and approves.
    const [customItems, setCustomItems] = useState([]);   // [{name, qty, unit, notes}]
    // Per-category fuzzy search — reset every time the user enters a new step
    const [catSearch, setCatSearch] = useState("");
    useEffect(() => { setCatSearch(""); }, [step]);

    // --- Load catalog, last order, favorites, and any existing draft in parallel ---
    useEffect(() => {
        setLoading(true);
        const reorderId = params.get("reorder_id");  // specific past invoice
        const reorderLast = params.get("reorder") === "1";
        Promise.all([
            portalApi.get("/items"),
            portalApi.get("/last-order"),
            portalApi.get("/favorites?top=8"),
            portalApi.get("/draft").catch(() => ({ data: null })),
            reorderId
                ? portalApi.get(`/reorder-from/${reorderId}`).catch(() => ({ data: null }))
                : Promise.resolve({ data: null }),
        ]).then(([c, l, f, d, r]) => {
            setCatalog(c.data);
            setLastOrder(l.data);
            setFavorites(f.data);
            // 1. Specific invoice reorder wins (?reorder_id=…)
            if (reorderId && r.data?.lines?.length) {
                prefillFromInvoice(r.data);
                setStep(STEP_REVIEW);
            }
            // 2. Legacy ?reorder=1 → last order (same exact-copy path)
            else if (reorderLast && l.data.invoice_id) {
                reorderInvoice(l.data.invoice_id);
            }
            // 3. Otherwise restore any saved draft
            else if (d.data?.cart && Object.keys(d.data.cart).length > 0) {
                setCart(d.data.cart);
                if (d.data.custom_items?.length) setCustomItems(d.data.custom_items);
                if (d.data.delivery_date_preference) setDeliveryDate(d.data.delivery_date_preference);
                if (d.data.notes) setNotes(d.data.notes);
                toast.info(`Draft restored · ${Object.keys(d.data.cart).length} items`);
            }
        }).finally(() => setLoading(false));
        /* eslint-disable-next-line */
    }, []);

    // Auto-save draft (debounced) whenever the cart, customItems, delivery date or notes change.
    useEffect(() => {
        if (loading) return;
        const t = setTimeout(() => {
            portalApi.put("/draft", {
                cart,
                custom_items: customItems,
                delivery_date_preference: deliveryDate,
                notes,
            }).catch(() => {});
        }, 800);
        return () => clearTimeout(t);
    }, [cart, customItems, deliveryDate, notes, loading]);

    // --- Cart helpers ---
    // Exact copy of a past invoice: every line, same order, same qty, same rate.
    const prefillFromInvoice = (data) => {
        const next = {};
        for (const l of data.lines) {
            let key = l.item_id || `inv-${l.seq}`;
            if (next[key]) key = `${key}#${l.seq}`;
            next[key] = {
                key, itemId: l.item_id, name: l.name, category: l.category || "Uncategorised",
                unit: l.unit || "Pack", rate: l.invoice_rate, invoiceRate: l.invoice_rate,
                currentRate: l.current_rate, tax_percent: l.tax_percent || 0, hsn: l.hsn || "",
                qty: Number(l.qty) || 0,
            };
        }
        setCart(next);
        setReorderOf({ id: data.invoice_id, number: data.invoice_number });
        toast.success(`Reordering all ${data.lines.length} items from ${data.invoice_number}`, { id: "reorder-prefill" });
    };
    const reorderInvoice = (invoiceId) => portalApi.get(`/reorder-from/${invoiceId}`)
        .then((r) => { prefillFromInvoice(r.data); setStep(STEP_REVIEW); })
        .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load that invoice"));

    const setQty = (item, qty) => {
        setCart(prev => {
            const n = { ...prev };
            if (!qty || qty <= 0) { delete n[item.id]; return n; }
            n[item.id] = {
                ...(prev[item.id] || {}),
                key: item.id, itemId: item.id, name: item.name, category: item.category || "Uncategorised",
                unit: item.unit || "Pack",
                rate: prev[item.id]?.invoiceRate ?? (item.price || 0),
                tax_percent: item.tax_percent || 0,
                hsn: item.hsn || "",
                qty: Number(qty),
            };
            return n;
        });
    };

    const bump = (item, delta) => {
        const current = cart[item.id]?.qty || 0;
        setQty(item, Math.max(0, current + delta));
    };

    // Map of previous quantities by item name (case-insensitive) — shown next to each row
    const prevQtyByName = useMemo(() => {
        const m = {};
        for (const l of (lastOrder.line_items || [])) {
            m[(l.name || "").toLowerCase()] = { qty: l.qty, unit: l.unit };
        }
        return m;
    }, [lastOrder]);

    // Running totals (budget preview)
    const totals = useMemo(() => {
        let subtotal = 0, tax = 0;
        Object.values(cart).forEach(l => {
            const lt = (l.qty || 0) * (l.rate || 0);
            subtotal += lt;
            tax += lt * (l.tax_percent || 0) / 100;
        });
        return {
            subtotal, tax,
            grand: subtotal + tax,
            itemCount: Object.keys(cart).length + (customItems?.length || 0),
        };
    }, [cart, customItems]);

    // --- Submit flow with "did you forget?" ---
    const proceedToReviewOrCheckForgot = () => {
        if (Object.keys(cart).length === 0 && (customItems?.length || 0) === 0) {
            toast.error("Add at least one item to your order");
            return;
        }
        // Compare with last order (custom items are always kept)
        const currentNames = new Set(Object.values(cart).map(l => l.name.toLowerCase()));
        const missing = (lastOrder.line_items || []).filter(l =>
            !currentNames.has((l.name || "").toLowerCase()));
        if (missing.length > 0 && Object.keys(cart).length > 0) {
            setForgotItems(missing);
            setForgotOpen(true);
        } else {
            setStep(STEP_REVIEW);
        }
    };

    const addForgotItem = (li) => {
        // Find in catalog by name
        const key = (li.name || "").toLowerCase();
        let found = null;
        for (const cat of catalog.categories) {
            for (const it of cat.items) {
                if ((it.name || "").toLowerCase() === key) { found = it; break; }
            }
            if (found) break;
        }
        if (!found) {
            toast.error(`${li.name} is no longer in the catalog — please add manually`);
            return;
        }
        setQty(found, li.qty || 1);
        setForgotItems(prev => prev.filter(x => x !== li));
    };

    // Voice-cart imperative handle so the "Speak your order" banner can
    // trigger recording without owning any audio state.
    const voiceRef = useRef(null);
    const startVoice = () => voiceRef.current?.start?.();

    // --- Voice-to-cart handlers (called by <PortalVoiceCart/> preview modal) ---
    // Matched items: add or merge qty into cart, keyed by the resolved item_id
    // so a second voice command for the same item stacks quantity rather than
    // overwrites — mirrors the manual +/- behaviour.
    const addVoiceMatched = (m) => {
        setCart(prev => {
            const existing = prev[m.item_id];
            return {
                ...prev,
                [m.item_id]: {
                    itemId: m.item_id, name: m.name,
                    category: m.category || "Uncategorised",
                    unit: m.unit || "Pack",
                    rate: m.rate || 0,
                    tax_percent: m.tax_percent || 0,
                    hsn: m.hsn || "",
                    qty: Number((existing?.qty || 0) + m.qty),
                },
            };
        });
    };
    const addVoiceCustom = (c) => {
        setCustomItems(prev => [...prev, {
            name: c.name, qty: Number(c.qty || 1),
            unit: c.unit || "packet", notes: c.notes || "",
        }]);
    };

    const submitOrder = async () => {
        setSubmitting(true);
        try {
            const line_items = Object.values(cart).map(l => ({
                item_id: l.itemId || "", name: l.name, hsn: l.hsn || "",
                qty: l.qty, unit: l.unit, rate: l.rate,
                category: l.category, tax_percent: l.tax_percent,
            }));
            // Filter empty custom rows (client added a row then never typed anything)
            const custom = customItems.filter(c => (c.name || "").trim() && (c.qty || 0) > 0);
            const r = await portalApi.post("/orders", {
                line_items,
                custom_items: custom,
                delivery_date_preference: deliveryDate,
                notes,
                invoice_type: catalog.invoice_type,
                reorder_of: reorderOf?.id || "",
            });
            const total = line_items.length + custom.length;
            toast.success(`Order submitted · ${total} item${total !== 1 ? "s" : ""}${custom.length ? ` (${custom.length} awaiting HDB confirmation)` : ""}`);
            setCart({}); setCustomItems([]); setReorderOf(null);   // clear local state; backend already cleared the draft
            nav("/portal/orders", { replace: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Order submission failed");
        } finally { setSubmitting(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="animate-spin mr-2" size={16}/> Loading catalog…
    </div>;

    // ---- INTRO STEP: category tiles + reorder + favorites ----
    if (step === STEP_INTRO) {
        return (
            <div className="space-y-8" data-testid="portal-order-intro">
                <div>
                    <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight" style={{fontFamily:"'Chivo',sans-serif"}}>
                        Let's build your order
                    </h1>
                    <p className="text-slate-500 mt-1">
                        We'll walk you through your usual categories one at a time. You can jump around freely too.
                    </p>
                </div>

                {/* Voice quick-add — big tap target (mobile + desktop) */}
                <button type="button" onClick={startVoice}
                        className="w-full rounded-2xl p-4 border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-purple-50/50
                                   hover:from-indigo-100 hover:to-purple-100 active:scale-[0.99] transition-all
                                   text-left"
                        data-testid="portal-voice-hint">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 text-white grid place-items-center shrink-0 shadow-lg ring-4 ring-emerald-200/60 animate-pulse">
                            <Mic size={26} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-black text-slate-900 text-base">Speak your order · Tap to start</span>
                                <span className="text-[9px] font-black uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded"
                                      data-testid="portal-voice-beta">
                                    Beta · under testing
                                </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">
                                Say something like "do kilo basmati chawal aur ek packet dhaniya"
                                <span className="block text-[10px] mt-0.5 text-slate-500 font-semibold">
                                    Hindi · Gujarati · English · Hinglish all work · may occasionally fumble or be slow
                                </span>
                            </div>
                        </div>
                    </div>
                </button>

                {/* Quick reorder */}
                {lastOrder.line_items?.length > 0 && (
                    <section className="rounded-2xl p-5 border border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50"
                             data-testid="portal-reorder-card">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center">
                                <Sparkles size={18}/>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900">Reorder your last order</div>
                                <div className="text-xs text-slate-600">
                                    {lastOrder.invoice_number} · {lastOrder.line_items.length} items · {lastOrder.invoice_date}
                                </div>
                            </div>
                            <button onClick={() => reorderInvoice(lastOrder.invoice_id)}
                                    className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm"
                                    data-testid="portal-reorder-btn">
                                Reorder
                            </button>
                        </div>
                    </section>
                )}

                {/* Favorites shortcut */}
                {favorites.length > 0 && (
                    <section data-testid="portal-favorites">
                        <div className="flex items-center gap-2 mb-3">
                            <Star size={16} className="text-amber-500 fill-amber-500" />
                            <h2 className="text-lg font-bold text-slate-900">Your favourites</h2>
                            <span className="text-xs text-slate-400">most-ordered items</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {favorites.slice(0, 6).map((f, i) => {
                                const master = findInCatalog(catalog, f.name);
                                if (!master) return null;
                                const inCart = cart[master.id]?.qty || 0;
                                return (
                                    <ItemMiniRow key={i} item={master} qty={inCart} lastQty={f.last_qty}
                                                 onChange={(q) => setQty(master, q)}
                                                 onBump={(d) => bump(master, d)}
                                                 data-testid={`portal-fav-${i}`} />
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Quick-add custom item — available at every stage */}
                <QuickAddCustom onAdd={(item) => setCustomItems([...customItems, item])} />

                {/* Category tiles */}
                <section data-testid="portal-category-tiles">
                    <h2 className="text-lg font-bold text-slate-900 mb-3">Browse by category</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {catalog.categories.map((cat, i) => {
                            const col = catColor(cat.name);
                            return (
                            <button key={i} onClick={() => setStep(i)}
                                    className={`group relative rounded-2xl border-2 ${col.border} ${col.bg} p-4 text-left shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all`}
                                    data-testid={`portal-cat-tile-${i}`}>
                                <div className="text-3xl mb-2">{catEmoji(cat.name)}</div>
                                <div className={`font-black text-base leading-tight ${col.text}`}
                                     style={{fontFamily:"'Chivo',sans-serif"}}>
                                    {cat.name}
                                </div>
                                <div className={`text-xs ${col.text} opacity-70 mt-0.5`}>{cat.items.length} items</div>
                                <ChevronRight className={`absolute top-4 right-4 ${col.text} opacity-40 group-hover:opacity-100 transition-opacity`} size={16}/>
                            </button>
                        );})}
                    </div>
                </section>

                {/* Sticky footer */}
                <StickyFooter totals={totals} onNext={() => catalog.categories.length && setStep(0)}
                              onReview={proceedToReviewOrCheckForgot} nextLabel="Start with first category" />
                <ForgotModal open={forgotOpen} items={forgotItems} onAdd={addForgotItem}
                             onClose={() => setForgotOpen(false)}
                             onContinue={() => { setForgotOpen(false); setStep(STEP_REVIEW); }} />
                <PortalVoiceCart ref={voiceRef} catalog={catalog}
                                 onAddToCart={addVoiceMatched}
                                 onAddCustom={addVoiceCustom} />
            </div>
        );
    }

    // ---- REVIEW STEP ----
    if (step === STEP_REVIEW) {
        const lines = Object.values(cart);
        return (
            <div className="space-y-6 pb-40" data-testid="portal-order-review">
                <button onClick={() => setStep(STEP_INTRO)} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                    <ChevronLeft size={14}/> Back to categories
                </button>
                <div>
                    <h1 className="text-3xl md:text-4xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                        Review your order
                    </h1>
                    <p className="text-slate-500 mt-1">Double-check quantities before submitting to HDB.</p>
                    {reorderOf && (
                        <p className="mt-2 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-block"
                           data-testid="portal-reorder-banner">
                            Exact copy of invoice {reorderOf.number} · {Object.keys(cart).length} items at the invoice rates. HDB will confirm any price changes.
                        </p>
                    )}
                </div>

                {/* Voice add-more mic — visible mid-flow so clients don't have to scroll back to intro */}
                <button type="button" onClick={startVoice}
                        className="w-full rounded-xl p-2.5 border border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50/60
                                   hover:from-indigo-100 hover:to-purple-100 active:scale-[0.99] transition-all
                                   flex items-center gap-2.5 text-left"
                        data-testid="portal-voice-hint-review">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 text-white grid place-items-center shrink-0 shadow-md ring-2 ring-emerald-200">
                        <Mic size={18}/>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <span className="font-bold text-sm text-slate-900">Add more by voice</span>
                            <span className="text-[8px] font-black uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 px-1 py-0.5 rounded">
                                Beta
                            </span>
                        </div>
                        <div className="text-[10px] text-slate-500">Hindi · Gujarati · English · Hinglish</div>
                    </div>
                </button>

                {/* Review — grouped by category so it mirrors how the final invoice reads */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                     data-testid="portal-review-grouped">
                    {(() => {
                        // Bucket cart lines by NORMALISED category (trim + case-fold)
                        // so subtle whitespace/case differences never split what should be one bucket.
                        const buckets = {};
                        const displayNames = {};
                        for (const l of lines) {
                            const raw = (l.category || "Uncategorised").trim();
                            const key = raw.toLowerCase();
                            (buckets[key] ||= []).push(l);
                            displayNames[key] = displayNames[key] || raw;
                        }
                        // Reorders keep the invoice's own category order.
                        const entries = reorderOf ? Object.entries(buckets)
                            : Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
                        if (entries.length === 0) {
                            return <div className="text-center text-slate-400 py-8 text-sm">Your cart is empty.</div>;
                        }
                        return entries.map(([catKey, catLines]) => {
                            const catName = displayNames[catKey];
                            const col = catColor(catName);
                            const catSub = catLines.reduce((s, l) => s + (l.qty || 0) * (l.rate || 0), 0);
                            return (
                                <div key={catKey} className="border-b border-slate-100 last:border-b-0"
                                     data-testid={`portal-review-cat-${catName}`}>
                                    <div className={`px-4 py-2.5 ${col.bg} ${col.text} flex items-center gap-2 border-l-4 ${col.border.replace("border-", "border-l-")}`}>
                                        <span className="text-lg">{catEmoji(catName)}</span>
                                        <span className="font-black text-sm uppercase tracking-wide"
                                              style={{fontFamily:"'Chivo',sans-serif"}}>{catName}</span>
                                        <span className="text-[11px] opacity-70 ml-1">· {catLines.length} item{catLines.length !== 1 ? "s" : ""}</span>
                                        <span className="ml-auto text-xs font-mono font-bold">{fmtINR(catSub)}</span>
                                    </div>
                                    <table className="w-full text-sm table-fixed">
                                        <colgroup><col/><col className="w-36"/><col className="w-24"/><col className="w-24"/><col className="w-8"/></colgroup>
                                        <thead className="text-slate-400 text-[10px] uppercase tracking-wider">
                                            <tr>
                                                <th className="text-left px-4 py-1">Item</th>
                                                <th className="text-right px-4 py-1">Qty</th>
                                                <th className="text-right px-4 py-1">Rate</th>
                                                <th className="text-right px-4 py-1">Total</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {catLines.map((l) => {
                                                const i = lines.indexOf(l);
                                                return (
                                                <tr key={l.key || l.itemId} data-testid={`portal-review-row-${i}`}>
                                                    <td className="px-4 py-1.5 font-semibold text-slate-900 text-sm">{l.name}</td>
                                                    <td className="px-4 py-1.5 text-right">
                                                        <input type="number" min="0" step="0.5" value={l.qty}
                                                               onChange={(e) => {
                                                                   const v = parseFloat(e.target.value) || 0;
                                                                   const k = l.key || l.itemId;
                                                                   setCart(prev => ({...prev, [k]: {...prev[k], qty: v}}));
                                                               }}
                                                               className="w-16 text-right border border-slate-200 rounded px-1.5 py-0.5 font-mono text-xs" />
                                                        <span className="text-[10px] text-slate-500 ml-1">{l.unit}</span>
                                                    </td>
                                                    <td className="px-4 py-1.5 text-right text-slate-600 font-mono text-xs">{fmtINR(l.rate)}</td>
                                                    <td className="px-4 py-1.5 text-right font-bold font-mono text-xs">{fmtINR((l.qty || 0) * (l.rate || 0))}</td>
                                                    <td className="px-4 py-1.5 text-right">
                                                        <button onClick={() => setCart(prev => { const n = {...prev}; delete n[l.key || l.itemId]; return n; })}
                                                                className="text-slate-400 hover:text-red-600 p-0.5"
                                                                data-testid={`portal-review-remove-${i}`}>
                                                            <X size={12}/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );})}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        });
                    })()}
                </div>

                {/* Custom items — free-typed items not in the catalog */}
                <CustomItemsSection items={customItems} setItems={setCustomItems} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <label className="block">
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
                                <CalendarDays size={12}/> Preferred delivery date
                            </div>
                            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)}
                                   className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                   data-testid="portal-delivery-date" />
                        </label>
                        <label className="block">
                            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
                                <ClipboardList size={12}/> Notes for HDB (optional)
                            </div>
                            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                      placeholder="Any special instructions?"
                                      data-testid="portal-notes" />
                        </label>
                    </div>
                    <div className="bg-slate-900 text-white rounded-2xl p-5 space-y-2">
                        <div className="text-xs uppercase tracking-wider text-slate-400">Summary (estimated)</div>
                        <SummaryRow label="Items" value={totals.itemCount} mono />
                        <SummaryRow label="Subtotal" value={fmtINR(totals.subtotal)} mono />
                        <SummaryRow label="GST" value={fmtINR(totals.tax)} mono />
                        <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between">
                            <div className="text-sm">Estimated total</div>
                            <div className="text-xl font-black font-mono" data-testid="portal-review-total">{fmtINR(totals.grand)}</div>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2">
                            Final invoice may vary slightly based on unit price at fulfilment.
                        </p>
                    </div>
                </div>

                <StickyFooter totals={totals} onNext={proceedToReviewOrCheckForgot}
                              onReview={submitOrder} submitting={submitting}
                              nextLabel="Add more items" reviewLabel="Submit order" />
                <ForgotModal open={forgotOpen} items={forgotItems} onAdd={addForgotItem}
                             onClose={() => setForgotOpen(false)}
                             onContinue={() => { setForgotOpen(false); submitOrder(); }}
                             continueLabel="Submit anyway" />
                <PortalVoiceCart ref={voiceRef} catalog={catalog}
                                 onAddToCart={addVoiceMatched}
                                 onAddCustom={addVoiceCustom} />
            </div>
        );
    }

    // ---- CATEGORY WIZARD STEPS ----
    const cat = catalog.categories[step];
    if (!cat) return <div>Category not found</div>;
    const isLast = step === catalog.categories.length - 1;

    // Fuzzy filter items within this category — tolerant to typos ("moot" → mustard oil).
    // We keep the Fuse index memoised per category so it isn't rebuilt on every keystroke.
    const fuse = new Fuse(cat.items, {
        keys: ["name", "hsn", "category"],
        threshold: 0.4,           // 0 = exact, 1 = matches anything · 0.4 = google-like tolerance
        ignoreLocation: true,
        minMatchCharLength: 2,
    });
    const visibleItems = catSearch.trim().length >= 2
        ? fuse.search(catSearch.trim()).map((r) => r.item)
        : cat.items;

    const col = catColor(cat.name);

    return (
        <div className="space-y-4 pb-40" data-testid={`portal-order-cat-${step}`}>
            <div className="flex items-center gap-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">
                    Step {step + 1} of {catalog.categories.length}
                </div>
                <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full ${col.chip.split(" ")[0]}`}
                         style={{width: `${((step+1)/catalog.categories.length)*100}%`}} />
                </div>
                <button onClick={() => setStep(STEP_INTRO)}
                        className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 font-semibold"
                        data-testid="portal-cat-back-top">
                    <Grid3x3 size={12}/> All categories
                </button>
            </div>

            {/* Colored category header band — bold, distinct, matches invoice format */}
            <div className={`rounded-2xl ${col.bg} border-2 ${col.border} px-5 py-4 flex items-center gap-4`}>
                <div className="text-4xl">{catEmoji(cat.name)}</div>
                <div className="flex-1 min-w-0">
                    <p className={`text-[10px] uppercase tracking-widest ${col.text} opacity-70 font-bold`}>Now ordering</p>
                    <h1 className={`text-2xl md:text-3xl font-black ${col.text} leading-tight truncate`}
                        style={{fontFamily:"'Chivo',sans-serif"}}>
                        {cat.name}
                    </h1>
                </div>
                <div className={`text-right ${col.text} shrink-0`}>
                    <div className="text-xs opacity-70">Items</div>
                    <div className="text-2xl font-black font-mono">{cat.items.length}</div>
                </div>
            </div>

            {/* Quick-add custom item — available at every wizard step */}
            <QuickAddCustom onAdd={(item) => setCustomItems([...customItems, item])} />

            {/* Compact voice mic — accessible mid-flow */}
            <button type="button" onClick={startVoice}
                    className="w-full rounded-xl p-2.5 border border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50/60
                               hover:from-indigo-100 hover:to-purple-100 active:scale-[0.99] transition-all
                               flex items-center gap-2.5 text-left"
                    data-testid="portal-voice-hint-cat">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 text-white grid place-items-center shrink-0 shadow-md ring-2 ring-emerald-200">
                    <Mic size={18}/>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm text-slate-900">Add by voice</span>
                        <span className="text-[8px] font-black uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 px-1 py-0.5 rounded">
                            Beta
                        </span>
                    </div>
                    <div className="text-[10px] text-slate-500">Hindi · Gujarati · English · Hinglish · may fumble occasionally</div>
                </div>
            </button>

            {/* Within-category fuzzy search */}
            <div className="relative">
                <Search className="absolute left-3 top-2.5 text-slate-400" size={14}/>
                <input value={catSearch} onChange={(e) => setCatSearch(e.target.value)}
                       placeholder={`Search ${cat.name.toLowerCase()} — spelling doesn't matter…`}
                       className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm shadow-sm"
                       data-testid="portal-cat-search" />
                {catSearch.trim().length >= 2 && (
                    <div className="text-[11px] text-slate-400 mt-1 ml-1">
                        {visibleItems.length} match{visibleItems.length !== 1 ? "es" : ""} for "<b>{catSearch}</b>"
                    </div>
                )}
            </div>

            {/* Compact single-column item rows — mirrors invoice line-item layout */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden"
                 data-testid="portal-cat-items">
                {visibleItems.map((it, i) => {
                    const inCart = cart[it.id]?.qty || 0;
                    const prev = prevQtyByName[(it.name || "").toLowerCase()];
                    return (
                        <ItemCompactRow key={it.id || i} item={it} qty={inCart}
                                        lastQty={prev?.qty} lastUnit={prev?.unit}
                                        onChange={(q) => setQty(it, q)}
                                        onBump={(d) => bump(it, d)}
                                        data-testid={`portal-item-${i}`} />
                    );
                })}
                {visibleItems.length === 0 && (
                    <div className="text-center text-slate-400 text-sm py-6"
                         data-testid="portal-cat-search-empty">
                        No matches. Try a shorter word — the search is spelling-tolerant.
                    </div>
                )}
            </div>

            {/* Bottom nav: Prev · All categories · Next */}
            <div className="grid grid-cols-3 items-center gap-3">
                <button onClick={() => setStep(step > 0 ? step - 1 : STEP_INTRO)}
                        className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold text-sm inline-flex items-center gap-1 justify-self-start"
                        data-testid="portal-cat-prev">
                    <ChevronLeft size={14}/> {step > 0 ? catalog.categories[step-1].name : "Overview"}
                </button>
                <button onClick={() => setStep(STEP_INTRO)}
                        className="px-4 py-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-600 font-bold text-sm inline-flex items-center gap-1 justify-self-center"
                        data-testid="portal-cat-back-center">
                    <Grid3x3 size={13}/> Back to all categories
                </button>
                {!isLast ? (
                    <button onClick={() => setStep(step + 1)}
                            className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm inline-flex items-center gap-1 justify-self-end"
                            data-testid="portal-cat-next">
                        Next: {catalog.categories[step+1].name} <ChevronRight size={14}/>
                    </button>
                ) : (
                    <button onClick={proceedToReviewOrCheckForgot}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold text-sm inline-flex items-center gap-1 justify-self-end"
                            data-testid="portal-cat-review">
                        Review order <ShieldCheck size={14}/>
                    </button>
                )}
            </div>

            <StickyFooter totals={totals} onNext={() => !isLast ? setStep(step+1) : proceedToReviewOrCheckForgot()}
                          onReview={proceedToReviewOrCheckForgot}
                          nextLabel={isLast ? "Review order" : `Next category`} />
            <ForgotModal open={forgotOpen} items={forgotItems} onAdd={addForgotItem}
                         onClose={() => setForgotOpen(false)}
                         onContinue={() => { setForgotOpen(false); setStep(STEP_REVIEW); }} />
            <PortalVoiceCart ref={voiceRef} catalog={catalog}
                             onAddToCart={addVoiceMatched}
                             onAddCustom={addVoiceCustom} />
        </div>
    );
}

/* ---- helpers & subcomponents ---- */

function findInCatalog(catalog, name) {
    const key = (name || "").toLowerCase();
    for (const cat of catalog.categories) {
        for (const it of cat.items) {
            if ((it.name || "").toLowerCase() === key) return it;
        }
    }
    return null;
}

export function catEmoji(name) {
    const s = (name || "").toLowerCase();
    if (/oil|fat/.test(s)) return "🫒";
    if (/rice|flour|starch/.test(s)) return "🌾";
    if (/spice|condiment|masala/.test(s)) return "🌶️";
    if (/pulse|dal/.test(s)) return "🫘";
    if (/dairy|milk/.test(s)) return "🥛";
    if (/clean|household/.test(s)) return "🧴";
    if (/tea|coffee|beverage/.test(s)) return "☕";
    if (/snack|biscuit/.test(s)) return "🍪";
    if (/personal|care/.test(s)) return "🧼";
    return "🛒";
}

// Distinct-but-tasteful palette rotated per category. Uses a deterministic
// hash on the category name so the SAME category always gets the SAME colour
// across intro tiles, wizard header, review sheet and past-order expand.
const CAT_PALETTE = [
    { bg: "bg-emerald-100", text: "text-emerald-900", border: "border-emerald-300", chip: "bg-emerald-600 text-white",   ring: "ring-emerald-300" },
    { bg: "bg-amber-100",   text: "text-amber-900",   border: "border-amber-300",   chip: "bg-amber-600 text-white",     ring: "ring-amber-300"   },
    { bg: "bg-sky-100",     text: "text-sky-900",     border: "border-sky-300",     chip: "bg-sky-600 text-white",       ring: "ring-sky-300"     },
    { bg: "bg-rose-100",    text: "text-rose-900",    border: "border-rose-300",    chip: "bg-rose-600 text-white",      ring: "ring-rose-300"    },
    { bg: "bg-violet-100",  text: "text-violet-900",  border: "border-violet-300",  chip: "bg-violet-600 text-white",    ring: "ring-violet-300"  },
    { bg: "bg-teal-100",    text: "text-teal-900",    border: "border-teal-300",    chip: "bg-teal-600 text-white",      ring: "ring-teal-300"    },
    { bg: "bg-orange-100",  text: "text-orange-900",  border: "border-orange-300",  chip: "bg-orange-600 text-white",    ring: "ring-orange-300"  },
    { bg: "bg-indigo-100",  text: "text-indigo-900",  border: "border-indigo-300",  chip: "bg-indigo-600 text-white",    ring: "ring-indigo-300"  },
];
export function catColor(name) {
    const s = (name || "Uncategorised").toLowerCase().trim();
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return CAT_PALETTE[Math.abs(h) % CAT_PALETTE.length];
}

function QuickAddCustom({ onAdd }) {
    // Compact inline row available in the intro + every category wizard step.
    // Client types a name + qty + optional unit and Adds. The item enters the
    // customItems array; admin will confirm HSN/GST/price before invoicing.
    // We debounce-classify on the name so the client sees the auto-detected
    // category/GST/HSN as they type — a small trust cue and helps admin later.
    const [name, setName] = useState("");
    const [qty, setQty] = useState("");
    const [unit, setUnit] = useState("Pack");
    const [flash, setFlash] = useState(false);
    const [hint, setHint] = useState(null);   // {category, hsn, tax_percent, source}
    const [classifying, setClassifying] = useState(false);
    const classifyRef = useRef(null);

    useEffect(() => {
        clearTimeout(classifyRef.current);
        if (!name.trim() || name.trim().length < 3) { setHint(null); return; }
        setClassifying(true);
        classifyRef.current = setTimeout(async () => {
            try {
                const r = await portalApi.get("/classify-item", { params: { name: name.trim() } });
                setHint(r.data.source === "unknown" ? null : r.data);
            } catch { setHint(null); }
            finally { setClassifying(false); }
        }, 550);
        return () => clearTimeout(classifyRef.current);
    }, [name]);

    const submit = (e) => {
        e?.preventDefault();
        if (!name.trim() || !qty) return;
        // Pipe the classifier hint into the payload so admin sees it too
        onAdd({
            name: name.trim(),
            qty: parseFloat(qty) || 1,
            unit: unit || "Pack",
            notes: hint ? `[auto] cat=${hint.category}, HSN=${hint.hsn}, GST=${hint.tax_percent}%, via=${hint.source}` : "",
        });
        setName(""); setQty(""); setHint(null);
        setFlash(true);
        setTimeout(() => setFlash(false), 1200);
    };

    return (
        <form onSubmit={submit}
              className={`rounded-2xl border-2 border-dashed p-3 flex flex-col gap-2
                          ${flash ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-slate-50/50"}`}
              data-testid="portal-quickadd-custom">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-lg pl-1">✍️</span>
                <span className="text-xs font-bold uppercase tracking-wide text-slate-600 pr-2">
                    Item not in this list?
                </span>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Type item name (e.g. Urad Sabut 500g)"
                       className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-slate-200 rounded-md"
                       data-testid="portal-quickadd-name" />
                <input type="number" min="0" step="0.5" value={qty}
                       onChange={(e) => setQty(e.target.value)} placeholder="Qty"
                       className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-md font-mono text-right"
                       data-testid="portal-quickadd-qty" />
                <input value={unit} onChange={(e) => setUnit(e.target.value)}
                       className="w-20 px-2 py-1.5 text-sm border border-slate-200 rounded-md"
                       data-testid="portal-quickadd-unit" />
                <button type="submit" disabled={!name.trim() || !qty}
                        className="hdb-btn hdb-btn-primary text-xs px-3 py-1.5 disabled:opacity-40"
                        data-testid="portal-quickadd-btn">
                    {flash ? "✓ Added" : <><Plus size={11}/> Add to order</>}
                </button>
            </div>
            {/* Auto-classify hint strip */}
            {(classifying || hint) && (
                <div className="pl-8 text-[11px] font-medium flex items-center gap-2 flex-wrap"
                     data-testid="portal-quickadd-hint">
                    {classifying ? (
                        <span className="text-slate-400">Detecting category & GST…</span>
                    ) : (
                        <>
                            <span className="text-slate-500">Auto-detected:</span>
                            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                {hint.category || "—"}
                            </span>
                            {hint.hsn && <span className="px-1.5 py-0.5 rounded bg-slate-100 font-mono text-slate-700">HSN {hint.hsn}</span>}
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-mono">GST {hint.tax_percent}%</span>
                            <span className="text-[10px] text-slate-400 italic">
                                {hint.source === "fuzzy" ? `matched ${hint.match_name}` : "via AI"}
                            </span>
                        </>
                    )}
                </div>
            )}
        </form>
    );
}

function ItemCompactRow({ item, qty, lastQty, lastUnit, onChange, onBump, ...rest }) {
    // Denser, invoice-line-style row. Name is the primary anchor,
    // last-order qty sits in a green cell to the right for quick reference,
    // qty controls sit at far right — everything on one line.
    return (
        <div className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors
                        ${qty > 0 ? "bg-emerald-50/30" : ""}`} {...rest}>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{item.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    <span className="font-mono font-bold text-slate-700">{fmtINR(item.price)}/{item.unit}</span>
                    {item.tax_percent > 0 && <span className="ml-2 text-slate-400">GST {item.tax_percent}%</span>}
                    {item.hsn && <span className="ml-2 text-slate-300 font-mono">HSN {item.hsn}</span>}
                </div>
            </div>
            {lastQty ? (
                <div className="hidden sm:flex flex-col items-end text-[10px] px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-800 shrink-0"
                     title="Your quantity last time">
                    <span className="uppercase tracking-wider opacity-70">Last</span>
                    <span className="font-mono font-black text-xs">{fmtQty(lastQty)} {lastUnit || item.unit}</span>
                </div>
            ) : null}
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => onBump(-1)} disabled={qty <= 0}
                        className="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-100 grid place-items-center text-slate-700 disabled:opacity-30"
                        data-testid="portal-qty-minus">
                    <Minus size={12}/>
                </button>
                <input type="number" min="0" step="0.5" value={qty || ""}
                       onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                       placeholder="0"
                       className="w-14 h-7 text-center rounded-md border border-slate-200 font-mono text-xs"
                       data-testid="portal-qty-input" />
                <button onClick={() => onBump(1)}
                        className="w-7 h-7 rounded-md border border-slate-200 hover:bg-slate-100 grid place-items-center text-slate-700"
                        data-testid="portal-qty-plus">
                    <Plus size={12}/>
                </button>
            </div>
        </div>
    );
}

function ItemMiniRow({ item, qty, lastQty, lastUnit, onChange, onBump, ...rest }) {
    return (
        <div className={`p-3 rounded-xl border ${qty > 0 ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"} flex items-center gap-3 hover:shadow-sm transition-all`} {...rest}>
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{item.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-slate-700">{fmtINR(item.price)}/{item.unit}</span>
                    {lastQty ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-bold">
                            Last: {fmtQty(lastQty)} {lastUnit || item.unit}
                        </span>
                    ) : null}
                    {item.tax_percent > 0 && <span className="text-slate-400">GST {item.tax_percent}%</span>}
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => onBump(-1)}
                        className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 grid place-items-center text-slate-700"
                        data-testid={`portal-qty-minus`} disabled={qty <= 0}>
                    <Minus size={14}/>
                </button>
                <input type="number" min="0" step="0.5" value={qty || ""}
                       onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                       placeholder="0"
                       className="w-16 h-8 text-center rounded-lg border border-slate-200 font-mono text-sm"
                       data-testid={`portal-qty-input`} />
                <button onClick={() => onBump(1)}
                        className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-100 grid place-items-center text-slate-700"
                        data-testid={`portal-qty-plus`}>
                    <Plus size={14}/>
                </button>
            </div>
        </div>
    );
}

function StickyFooter({ totals, onNext, onReview, nextLabel = "Continue", reviewLabel = "Review order", submitting = false }) {
    return (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur border-t border-slate-200 shadow-2xl">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <ShoppingCart size={16} className="text-slate-500 shrink-0" />
                    <div className="text-sm text-slate-500">
                        <span className="font-bold text-slate-900">{totals.itemCount}</span> items
                        <span className="mx-2 text-slate-300">·</span>
                        <span className="font-black text-emerald-700 font-mono" data-testid="portal-running-total">{fmtINR(totals.grand)}</span>
                        <span className="text-slate-400 text-xs ml-1">estimated</span>
                    </div>
                </div>
                <button onClick={onReview} disabled={submitting || totals.itemCount === 0}
                        className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold text-sm disabled:opacity-40 inline-flex items-center gap-1"
                        data-testid="portal-footer-review">
                    {submitting ? <><Loader2 className="animate-spin" size={14}/> Submitting…</> : <><ShieldCheck size={14}/> {reviewLabel}</>}
                </button>
            </div>
        </div>
    );
}

function ForgotModal({ open, items, onAdd, onClose, onContinue, continueLabel = "Continue without adding" }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="portal-forgot-modal">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                        <AlertTriangle size={18}/>
                    </div>
                    <div className="flex-1">
                        <h3 className="font-black text-slate-900 text-lg" style={{fontFamily:"'Chivo',sans-serif"}}>Did you forget something?</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            You ordered these last time but haven't added them this time. Add any you still need?
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={16}/></button>
                </div>
                <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                    {items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">You've added everything from last order</div>}
                    {items.map((li, i) => (
                        <div key={i} className="flex items-center gap-3 p-2 border border-slate-200 rounded-lg"
                             data-testid={`portal-forgot-item-${i}`}>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-slate-900 text-sm truncate">{li.name}</div>
                                <div className="text-xs text-slate-500">Last: {fmtQty(li.qty)} {li.unit}</div>
                            </div>
                            <button onClick={() => onAdd(li)}
                                    className="text-xs px-3 py-1 rounded-lg bg-slate-900 text-white font-bold"
                                    data-testid={`portal-forgot-add-${i}`}>
                                <Plus size={11} className="inline"/> Add
                            </button>
                        </div>
                    ))}
                </div>
                <div className="mt-4 flex gap-2 justify-end">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-200">Keep editing</button>
                    <button onClick={onContinue} className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white font-bold"
                            data-testid="portal-forgot-continue">
                        {continueLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

function SummaryRow({ label, value, mono }) {
    return (
        <div className="flex justify-between text-sm">
            <div className="text-slate-400">{label}</div>
            <div className={`text-white ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

/**
 * CustomItemsSection — free-typed items the client wants that aren't in the
 * catalog. Client types name + qty + unit. Admin will fill HSN/GST/price and
 * confirm before it becomes a real line item on the invoice.
 */
function CustomItemsSection({ items, setItems }) {
    const add = () => setItems([...items, { name: "", qty: 1, unit: "Pack", notes: "" }]);
    const update = (i, patch) => setItems(items.map((x, j) => j === i ? { ...x, ...patch } : x));
    const remove = (i) => setItems(items.filter((_, j) => j !== i));

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
             data-testid="portal-custom-items-section">
            <div className="px-4 py-2.5 bg-slate-900 text-white flex items-center gap-2 border-l-4 border-l-amber-400">
                <span className="text-lg">✍️</span>
                <span className="font-black text-sm uppercase tracking-wide" style={{fontFamily:"'Chivo',sans-serif"}}>
                    Need something we don't stock?
                </span>
                <span className="text-[11px] opacity-70 ml-1">
                    Type it here — HDB will confirm pricing & GST before invoicing
                </span>
                <button onClick={add}
                        className="ml-auto px-3 py-1 rounded-md bg-white text-slate-900 text-xs font-bold inline-flex items-center gap-1 hover:bg-slate-100"
                        data-testid="portal-add-custom-item">
                    <Plus size={11}/> Add row
                </button>
            </div>
            {items.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-xs">
                    No custom items — click <b>Add row</b> above if you need something outside the categories.
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-slate-400 text-[10px] uppercase tracking-wider bg-slate-50/50">
                        <tr>
                            <th className="text-left px-4 py-1.5">Item name *</th>
                            <th className="text-right px-4 py-1.5">Qty *</th>
                            <th className="text-left px-4 py-1.5">Unit</th>
                            <th className="text-left px-4 py-1.5">Notes to HDB</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {items.map((it, i) => (
                            <tr key={i} data-testid={`portal-custom-row-${i}`}>
                                <td className="px-4 py-1.5">
                                    <input value={it.name} onChange={(e) => update(i, { name: e.target.value })}
                                           placeholder="e.g. Organic quinoa 500g"
                                           className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                           data-testid={`portal-custom-name-${i}`} />
                                </td>
                                <td className="px-4 py-1.5 text-right">
                                    <input type="number" min="0" step="0.5" value={it.qty}
                                           onChange={(e) => update(i, { qty: parseFloat(e.target.value) || 0 })}
                                           className="w-20 text-right px-2 py-1 text-sm border border-slate-200 rounded font-mono"
                                           data-testid={`portal-custom-qty-${i}`} />
                                </td>
                                <td className="px-4 py-1.5">
                                    <input value={it.unit} onChange={(e) => update(i, { unit: e.target.value })}
                                           className="w-20 px-2 py-1 text-sm border border-slate-200 rounded"
                                           placeholder="Pack" />
                                </td>
                                <td className="px-4 py-1.5">
                                    <input value={it.notes} onChange={(e) => update(i, { notes: e.target.value })}
                                           className="w-full px-2 py-1 text-sm border border-slate-200 rounded"
                                           placeholder="Any brand / size preference" />
                                </td>
                                <td className="px-4 py-1.5 text-right">
                                    <button onClick={() => remove(i)}
                                            className="text-slate-300 hover:text-red-600 p-0.5">
                                        <X size={12}/>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

